import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { TanStackQueryIds } from '../../query/query-ids'
import { deleteElemental } from '~~/src/api/elementals'
import type { Elemental, ElementalTypeRoutes } from '~~/src/types/elemental'
import { ElementalTypeEnum } from '~~/src/types/elemental'
import type { List } from '~~/src/types/list'
import { useSidebarStore } from '~~/src/stores/sidebar'

export interface UseDeleteElementalArgs { elementalType: keyof typeof ElementalTypeRoutes; id: string; listId?: string; listType?: ElementalTypeEnum }

export const useDeleteElemental = () => {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation<boolean, Error, UseDeleteElementalArgs>({
    mutationKey: ['delete-elemental'],
    mutationFn: async ({ id }) => {
      const result = await deleteElemental(id)

      return result
    },
    onMutate: async ({ elementalType, id, listId, listType }) => {
      const store = useSidebarStore()
      if (store.state.currentElemental?.id === id)
        store.goToView()

      useDispatchCustomEvent('elemental:delete', { elementId: id! })
      const isOnAll = !listId || listId.toString().includes('all') || listId === getElementalTypeRoute(elementalType)
      const isList = elementalType === ElementalTypeEnum.List

      try {
        queryClient.setQueryData([TanStackQueryIds.FAVORITES], (old: any) => {
          if (!old)
            return old

          return old.filter((e: any) => e.id !== id)
        })

        queryClient.setQueriesData<Elemental[]>({
          queryKey: [TanStackQueryIds.RECENT_ELEMENTAL],
          exact: false,
        }, (old) => {
          if (!old)
            return old

          return old.filter((e: any) => e.id !== id)
        })

        if (!isList && isOnAll) {
          queryClient.setQueriesData<List[]>({
            queryKey: ['profile-list-elementals', elementalType],
            exact: false,
          }, (old) => {
            if (!old)
              return old
            const AllList = old.shift()
            if (!AllList || !AllList?.prompts)
              return old
            AllList.prompts = AllList.prompts.filter(e => e.id !== id)
            return [{ ...AllList }, ...old]
          })
          return
        }
        if (isList && isOnAll) {
          const typeId = listType!
          queryClient.setQueriesData<List[]>({
            queryKey: ['profile-list-elementals', typeId],
            exact: false,
          }, (old) => {
            if (!old)
              return old
            return old.filter(list => list.id !== id)
          })
          return
        }
        queryClient.setQueriesData<Elemental[]>({
          queryKey: [TanStackQueryIds.LIST_ELEMENTALS, listId],
          exact: false,
        }, (old) => {
          if (!old || !old.length)
            return old
          return old.filter(e => e.id !== id)
        })
      }
      catch (error) {
      }
    },
    onError: (_, { elementalType, listId, listType }) => {
      const isOnAll = !listId || listId.toString().includes('all') || listId === getElementalTypeRoute(elementalType)
      const isList = elementalType === ElementalTypeEnum.List
      const elementalLabel = `${capitalizeFirstLetter(getElementalTypeRoute(elementalType))}`

      if (isOnAll && !isList) {
        queryClient.refetchQueries({
          queryKey: ['profile-list-elementals', elementalType],
          exact: false,
          type: 'all',
        })
        return
      }
      if (isOnAll && isList) {
        queryClient.refetchQueries({
          queryKey: ['profile-list-elementals', listType],
          exact: false,
          type: 'all',
        })
        return
      }
      queryClient.refetchQueries({
        queryKey: [TanStackQueryIds.LIST_ELEMENTALS, listId],
        exact: false,
      })
    },
    onSuccess: (_, { elementalType, listId, listType }) => {
      const elementalLabel = `${capitalizeFirstLetter(getElementalTypeRoute(elementalType))}`
      toast.success(`${elementalLabel} deleted`)
      const isOnAll = !listId || listId.toString().includes('all') || listId === getElementalTypeRoute(elementalType)
      const isList = elementalType === ElementalTypeEnum.List
      if (isOnAll && !isList) {
        queryClient.invalidateQueries({
          queryKey: ['profile-list-elementals', elementalType],
          exact: false,
          type: 'all',
        })
        return
      }
      if (isOnAll && isList) {
        queryClient.invalidateQueries({
          queryKey: ['profile-list-elementals', listType],
          exact: false,
          type: 'all',
        })
        return
      }
      queryClient.invalidateQueries({
        queryKey: [TanStackQueryIds.LIST_ELEMENTALS, listId],
        exact: false,
      })
    },
  })
}
